import { useEffect, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  X, LogOut, Building2, Check, ChevronRight,
  LayoutDashboard, ListChecks,
  Inbox, Bell, AppWindow,
  Users, UsersRound, Palette,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useTenant } from "@/context/tenant-context";
import { useAuth } from "@/context/auth-context";
import { companiesApi } from "@/api/companies";
import { queryKeys } from "@/lib/query-keys";
import { ThemePicker } from "@/components/theme-picker";

interface MobileDrawerProps {
  open: boolean;
  onClose: () => void;
}

// Secondary nav shown under the mobile "More" drawer.
// Hidden at this stage (routes still exist, just unlisted): goals, inbox,
// agent-comms. Costs, Activity, Hiring, Approvals and Artifacts were removed
// entirely on 2026-09-26.
const SECONDARY_NAV = [
  { path: "dashboard", label: "Dashboard", icon: LayoutDashboard },
  { path: "apps", label: "Apps", icon: AppWindow },
  { path: "tasks", label: "Tasks", icon: ListChecks },
  { path: "issues", label: "Issues", icon: Inbox },
  { path: "notifications", label: "Notifications", icon: Bell },
  { path: "users", label: "Users", icon: Users },
  { path: "groups", label: "Groups", icon: UsersRound },
] as const;

export function MobileDrawer({ open, onClose }: MobileDrawerProps) {
  const { tenantId, tenantName, isInstanceAdmin } = useTenant();
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const [themePickerOpen, setThemePickerOpen] = useState(false);
  const [tenantSwitcherOpen, setTenantSwitcherOpen] = useState(false);

  // Lock body scroll when drawer is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  // Load all tenants for instance admins (lazy — only when switcher opens)
  const { data: allCompanies = [] } = useQuery({
    queryKey: queryKeys.companies.all,
    queryFn: companiesApi.list,
    enabled: isInstanceAdmin && tenantSwitcherOpen,
  });

  const base = `/tenants/${tenantId}`;

  if (!open) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 z-40 bg-black/50 sm:hidden"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="glass-raised fixed inset-y-0 left-0 z-50 flex w-[85vw] max-w-[320px] flex-col rounded-r-[28px] border-l-0 sm:hidden animate-in slide-in-from-left">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-border shrink-0">
          <span className="text-sm font-semibold text-text-primary truncate">{tenantName}</span>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-surface-hover text-text-muted hover:text-text-primary"
          >
            <X size={18} />
          </button>
        </div>

        {/* Scrollable content */}
        <div className="flex-1 overflow-y-auto">
          {/* Tenant switcher (instance admins only) */}
          {isInstanceAdmin && (
            <div className="border-b border-border">
              <button
                onClick={() => setTenantSwitcherOpen(!tenantSwitcherOpen)}
                className="flex w-full items-center justify-between px-4 py-3 hover:bg-surface-hover"
              >
                <div className="flex items-center gap-2.5">
                  <Building2 size={16} className="text-text-muted" />
                  <span className="text-sm font-medium text-text-primary">Switch Tenant</span>
                </div>
                <ChevronRight
                  size={14}
                  className={cn(
                    "text-text-muted transition-transform",
                    tenantSwitcherOpen && "rotate-90",
                  )}
                />
              </button>
              {tenantSwitcherOpen && (
                <div className="bg-background/40 pb-2">
                  {allCompanies.map((c) => (
                    <button
                      key={c.id}
                      onClick={() => {
                        navigate(`/tenants/${c.id}/workspace`);
                        onClose();
                      }}
                      className={cn(
                        "flex w-full items-center gap-2.5 px-6 py-2 text-left hover:bg-surface-hover",
                        c.id === tenantId && "bg-accent/5",
                      )}
                    >
                      <Building2 size={13} className="text-text-muted shrink-0" />
                      <span className="text-xs text-text-primary flex-1 truncate">{c.name}</span>
                      {c.id === tenantId && <Check size={13} className="text-accent shrink-0" />}
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Secondary navigation */}
          <nav className="py-2">
            {SECONDARY_NAV.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.path}
                  to={`${base}/${item.path}`}
                  onClick={onClose}
                  className={({ isActive }) =>
                    cn(
                      "flex items-center gap-2.5 px-4 py-2.5 text-sm transition-colors",
                      isActive
                        ? "bg-accent/10 text-accent border-l-2 border-accent"
                        : "text-text-secondary hover:bg-surface-hover hover:text-text-primary",
                    )
                  }
                >
                  <Icon size={16} />
                  <span>{item.label}</span>
                </NavLink>
              );
            })}
          </nav>
        </div>

        {/* Footer */}
        <div className="border-t border-border px-4 py-3 shrink-0">
          {user && (
            <div className="mb-2 flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-xs font-medium text-accent">
                {user.name?.[0]?.toUpperCase() || "?"}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-medium text-text-primary truncate">{user.name}</p>
                <p className="text-[10px] text-text-muted truncate">{user.email}</p>
              </div>
            </div>
          )}
          <div className="flex gap-1.5">
            <button
              onClick={() => setThemePickerOpen(true)}
              className="glass-field flex flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs font-medium text-text-secondary"
            >
              <Palette size={13} />
              Theme
            </button>
            <button
              onClick={() => {
                signOut();
                onClose();
              }}
              className="glass-field flex flex-1 items-center justify-center gap-1.5 rounded-full px-2 py-2 text-xs font-medium text-[#E5484D]"
            >
              <LogOut size={13} />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {themePickerOpen && <ThemePicker onClose={() => setThemePickerOpen(false)} />}
    </>
  );
}
